import type { ScoreBreakdownData } from '../../apiHooks/useScoreBreakdownByUserQuery';
import { MAX_CYBER_IQ_POINTS } from '../../consts/cyberIQ';

const clampToScoreRange = (score: number) => Math.min(MAX_CYBER_IQ_POINTS, Math.max(0, score));

/**
 * How much the learner's CQ total moved over the summary period.
 *
 * The backend clamps the stored total to [0, MAX_CYBER_IQ_POINTS] after every event, so the raw sum of this
 * period's scores overstates any change the clamp absorbed: a learner on 0 who loses 250 stays on 0. No score
 * history exists, so the total at the start of the period is estimated as the clamped all-time raw sum minus this
 * period's raw sum. It is exact unless the clamp also cut in before the period started.
 *
 * Only `scoreBreakdownAggregations` is summed: `dynamicActionAggregations` re-lists actions whose scores are
 * already counted in their category, so adding it double counts them.
 */
export const getMonthlyScoreDelta = (
  currentTotal: number,
  scoreBreakdownData: Pick<ScoreBreakdownData, 'scoreBreakdownAggregations'> | undefined,
): number => {
  const aggregations = scoreBreakdownData?.scoreBreakdownAggregations;
  if (!aggregations?.length) {
    return 0;
  }

  const periodScore = aggregations.reduce((sum, item) => sum + item.score, 0);
  const allTimeScore = aggregations.reduce((sum, item) => sum + item.globalScore, 0);
  const totalAtPeriodStart = clampToScoreRange(allTimeScore - periodScore);

  return clampToScoreRange(currentTotal) - totalAtPeriodStart;
};
